import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_STOCK_JUMP_POSITIONS, SEARCH_STOCKS } from '../graphql/queries/stocks';
import { stockRoutePath } from '../constants/routes';
import { useTheme } from './ThemeProvider';
import { usePortfolioContext } from '../contexts/PortfolioContext';

const EMPTY_RESULTS = [];
const listingKey = (stock) => `${stock.symbol?.trim().toUpperCase() || ''}:${stock.exchange?.trim().toUpperCase() || ''}`;

const StockJumpSearch = ({ portfolioId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remoteResults, setRemoteResults] = useState({ query: '', items: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [searchStocks] = useLazyQuery(SEARCH_STOCKS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore',
  });

  const { data: positionsData, loading: positionsLoading, error: positionsError, refetch: refetchPositions } = useQuery(GET_STOCK_JUMP_POSITIONS, {
    variables: { portfolioId },
    skip: !portfolioId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const positions = positionsData?.stockJumpPositions || EMPTY_RESULTS;
  const trimmedQuery = query.trim();
  const results = useMemo(() => {
    const term = trimmedQuery.toLowerCase();
    const local = positions.filter((stock) => !term || [stock.name, stock.symbol].some((value) => value?.toLowerCase().includes(term)));
    const byId = new Map(positions.filter((stock) => stock.stockId).map((stock) => [stock.stockId, stock]));
    const byListing = new Map(positions.map((stock) => [listingKey(stock), stock]));
    const seenIds = new Set();
    const seenListings = new Set();
    const owned = [];
    const other = [];
    const remote = remoteResults.query === trimmedQuery ? remoteResults.items : EMPTY_RESULTS;
    for (const stock of [...local, ...remote]) {
      const known = byId.get(stock.stockId) || byListing.get(listingKey(stock));
      const item = { ...stock, ...(known || {}), hasPosition: Boolean(known) };
      const key = listingKey(item);
      if ((item.stockId && seenIds.has(item.stockId)) || seenListings.has(key)) continue;
      if (item.stockId) seenIds.add(item.stockId);
      seenListings.add(key);
      (item.hasPosition ? owned : other).push(item);
    }
    return [...owned, ...other];
  }, [positions, remoteResults, trimmedQuery]);

  // Refresh the cheap membership preload on opening, without hiding cached positions.
  useEffect(() => {
    if (isOpen && portfolioId) refetchPositions().catch(() => {});
  }, [isOpen, portfolioId, refetchPositions]);

  const close = () => {
    setIsOpen(false);
    setQuery('');
    setRemoteResults({ query: '', items: [] });
    setSelectedIndex(0);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'j' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setIsOpen(true);
      } else if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        event.stopImmediatePropagation();
        close();
      }
    };
    const handleOpenSearch = () => setIsOpen(true);

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('open-search', handleOpenSearch);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('open-search', handleOpenSearch);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
    if (!isOpen || trimmedQuery.length < 2) {
      setIsSearching(false);
      setRemoteResults({ query: '', items: [] });
      return undefined;
    }
    let active = true;
    setIsSearching(true);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await searchStocks({ variables: { query: trimmedQuery } });
        if (active) setRemoteResults({ query: trimmedQuery, items: response.data?.searchStocks || [] });
      } catch (_) {
        // The local portfolio matches remain usable when universal search fails.
        if (active) setRemoteResults({ query: trimmedQuery, items: [] });
      } finally {
        if (active) setIsSearching(false);
      }
    }, 500);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [isOpen, trimmedQuery, searchStocks]);

  useEffect(() => {
    setSelectedIndex((current) => Math.max(0, Math.min(current, results.length - 1)));
  }, [results.length]);

  const selectResult = (stock) => {
    navigate(stockRoutePath(stock), { state: { stock } });
    close();
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((current) => Math.max(0, Math.min(current + 1, results.length - 1)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter' && results[selectedIndex]) {
      event.preventDefault();
      selectResult(results[selectedIndex]);
    }
  };

  const buttonClasses = darkMode
    ? 'flex items-center justify-center p-2 rounded-full bg-gray-800 hover:bg-gray-700 shadow-lg'
    : 'flex items-center justify-center p-2 rounded-full bg-white hover:bg-gray-100 shadow-lg border border-gray-200';

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={buttonClasses}
        aria-label="Jump to stock (⌘J)"
      >
        <span className="sr-only">Search stocks</span>
        <svg className={`h-5 w-5 ${darkMode ? 'text-yellow-400' : 'text-blue-600'}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-start justify-center px-3 pt-20" onClick={close}>
          <div role="dialog" aria-modal="true" aria-label="Jump to stock" className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg overflow-hidden" style={{ maxHeight: '80dvh' }} onClick={(event) => event.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <svg className="h-5 w-5 text-gray-400 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search stocks, ETFs, and funds..."
                className="min-w-0 flex-1 bg-transparent border-none outline-none text-gray-700 dark:text-gray-200"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleInputKeyDown}
              />
              {(isSearching || positionsLoading) && <div className="animate-spin h-4 w-4 rounded-full border-2 border-gray-300 border-t-blue-500" />}
              <button type="button" onClick={close} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" aria-label="Close search">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {positionsError && <p className="px-4 py-2 text-xs text-amber-700 dark:text-amber-400">Portfolio positions could not be refreshed. Search is still available.</p>}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(80dvh - 60px)' }}>
              {results.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {positionsLoading || isSearching ? 'Searching...' : trimmedQuery.length >= 2 ? 'No instruments found' : 'Type to search stocks, ETFs, and funds'}
                </div>
              ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {results.map((stock, index) => (
                    <li
                      key={`${stock.symbol}:${stock.exchange}`}
                      className={`p-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center ${index === selectedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
                      onClick={() => selectResult(stock)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 dark:text-gray-200 truncate">
                          {stock.name} ({stock.symbol})
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {[stock.exchange, stock.instrumentType?.toUpperCase(), stock.currency].filter(Boolean).join(' · ')}
                          {stock.hasPosition && <span className="ml-2 text-blue-600 dark:text-blue-400">Position</span>}
                        </div>
                      </div>
                      <svg className="h-5 w-5 text-gray-400 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Reset pending searches and membership whenever the active portfolio changes.
const StockJump = () => {
  const { currentPortfolioId } = usePortfolioContext();
  return <StockJumpSearch key={currentPortfolioId || 'none'} portfolioId={currentPortfolioId} />;
};

export default StockJump;
